import { useState } from "react";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { PRODUCTS } from "../data/products";
import {
  Box,
  Typography,
  Input,
  Select,
  Option,
  Textarea,
  Button,
} from "@mui/joy";

const fieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: 0,
    fontSize: "0.875rem", // text-sm
    color: "var(--ink)",
    "& fieldset": {
      borderColor: "rgba(var(--ink-rgb), 0.2)", // border-ink/20
    },
    "&:hover fieldset": {
      borderColor: "rgba(var(--ink-rgb), 0.2)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "var(--safety)",
      borderWidth: "2px",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "14px 16px", // px-4 py-3.5
    "&::placeholder": {
      color: "rgba(var(--muted-foreground-rgb), 0.6)",
      opacity: 1,
    },
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: "rgba(var(--muted-foreground-rgb), 0.6)",
    opacity: 1,
  },
};

// mono-label equivalent
const labelSx = {
  display: "block",
  mb: 1, // mb-2 (approx, MUI spacing unit = 8px -> theme dependent)
  color: "var(--ink)",
  fontFamily: "var(--font-mono, monospace)",
  fontSize: "0.75rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  fontWeight: 600,
};

export function QuoteForm() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    product: PRODUCTS[0].name,
    phase: "Single Phase",
    power_rating: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_ACCESS_KEY,

          subject: "New Quote Request - ITL Technical Services",
          from_name: "ITL Technical Services Website",

          name: form.name,
          company: form.company,
          email: form.email,
          phone: form.phone,
          product: form.product,
          phase: form.phase,
          power_rating: form.power_rating,
          message: form.message,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setDone(true);

        setForm({
          name: "",
          company: "",
          email: "",
          phone: "",
          product: PRODUCTS[0].name,
          phase: "Single Phase",
          power_rating: "",
          message: "",
        });
      } else {
        alert("Failed to send request.");
        console.error(result);
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "1fr 1fr",
        },
        gap: 2.5,
      }}
    >
      <Box>
        <Typography sx={labelSx}>Name *</Typography>
        <Input
          value={form.name}
          onChange={set("name")}
          placeholder="Full name"
        />
      </Box>

      <Box>
        <Typography sx={labelSx}>Company</Typography>
        <Input
          value={form.company}
          onChange={set("company")}
          placeholder="Organisation"
        />
      </Box>

      <Box>
        <Typography sx={labelSx}>Email *</Typography>
        <Input
          type="email"
          value={form.email}
          onChange={set("email")}
          placeholder="you@company.com"
        />
      </Box>

      <Box>
        <Typography sx={labelSx}>Phone</Typography>
        <Input
          value={form.phone}
          onChange={set("phone")}
          placeholder="+91..."
        />
      </Box>

      <Box>
        <Typography sx={labelSx}>Product Interest *</Typography>
        <Select
          value={form.product}
          onChange={(_, value) => setForm((f) => ({ ...f, product: value }))}
        >
          {PRODUCTS.map((p) => (
            <Option key={p.slug} value={p.name}>
              {p.short}
            </Option>
          ))}
        </Select>
      </Box>

      <Box>
        <Typography sx={labelSx}>Phase</Typography>
        <Select
          value={form.phase}
          onChange={(_, value) => setForm((f) => ({ ...f, phase: value }))}
        >
          <Option value="Single Phase">Single Phase</Option>
          <Option value="Three Phase">Three Phase</Option>
          <Option value="Not sure">Not sure</Option>
        </Select>
      </Box>

      <Box sx={{ gridColumn: "1 / -1" }}>
        <Typography sx={labelSx}>Power Rating (kVA)</Typography>
        <Input
          value={form.power_rating}
          onChange={set("power_rating")}
          placeholder="e.g. 10 kVA / 50 kVA"
        />
      </Box>

      <Box sx={{ gridColumn: "1 / -1" }}>
        <Typography sx={labelSx}>Message</Typography>
        <Textarea
          minRows={5}
          value={form.message}
          onChange={set("message")}
          placeholder="Load details, input voltage range, site conditions..."
        />
      </Box>

      <Box sx={{ gridColumn: "1 / -1" }}>
        <Button
          type="submit"
          loading={loading}
          endDecorator={<ArrowRight size={18} />}
          sx={{
            borderRadius: 0,
            px: 4,
            py: 1.5,
            bgcolor: "#FF5A1F",
            "&:hover": {
              bgcolor: "#111114",
            },
          }}
        >
          SEND REQUEST
        </Button>
      </Box>
    </Box>
  );
}
